import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { authClient, useSession } from "@/apps/web/lib/auth-client";
import { useTheme } from "@/apps/web/hooks/use-theme";
import { cn } from "@/packages/utils/cn";
import { EdLogo } from "./EdLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/apps/web/components/ui/dropdown-menu";
import { LogOut, Moon, Settings, Sun, ChevronsUpDown } from "lucide-react";

export interface Team {
  id: string;
  referenceId: string;
  name: string;
  slug: string;
  isPersonal: boolean;
  role?: "owner" | "admin" | "member";
}

export interface Project {
  id: string;
  referenceId: string;
  name: string;
  slug: string;
  teamId: string;
  color?: string;
  description?: string | null;
}

interface Props {
  teams: Team[];
  projects: Record<string, Project[]>;
  onCreateProject?: (teamId: string) => void;
  onCreateTeam?: () => void;
  onLogout?: () => void;
  className?: string;
}

const TOOLS: Array<{
  id: string;
  label: string;
  n: string;
  pathSuffix: string;
}> = [
  { id: "home", label: "Project overview", n: "00", pathSuffix: "" },
  { id: "release", label: "Releases", n: "01", pathSuffix: "/releases" },
  { id: "flags", label: "Feature flags", n: "02", pathSuffix: "/flags" },
  { id: "mock", label: "API mocks", n: "03", pathSuffix: "/mocks" },
  { id: "webhook", label: "Webhook inbox", n: "04", pathSuffix: "/webhooks" },
];

export function AppSidebar({
  teams,
  projects,
  onCreateProject,
  onCreateTeam,
  onLogout,
  className,
}: Props) {
  const { data: session } = useSession();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { teamId, projectId } = useParams();
  const location = useLocation();

  const currentTeam = teams.find((t) => t.id === teamId) ?? teams[0];
  const teamProjects = currentTeam ? projects[currentTeam.id] ?? [] : [];
  const currentProject = teamProjects.find((p) => p.referenceId === projectId);

  const isActiveTool = (suffix: string): boolean => {
    if (!currentProject) return false;
    const base = `/teams/${currentTeam!.id}/projects/${currentProject.referenceId}`;
    if (suffix === "") {
      return location.pathname === base;
    }
    return location.pathname.startsWith(base + suffix);
  };

  return (
    <aside
      className={cn(
        "w-[246px] bg-card-paper border-r border-rule flex flex-col shrink-0 h-full",
        className,
      )}
    >
      <div className="px-4 pt-5 pb-3">
        <Link to="/teams">
          <EdLogo />
        </Link>
      </div>

      {/* Team picker */}
      <div className="px-3 pb-3">
        <div className="text-eyebrow px-1 pb-1.5">Team</div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-2 p-2 rounded-md border border-rule bg-paper hover:bg-panel transition-colors text-left">
              <span
                className="size-5 rounded bg-ink text-paper text-[10.5px] font-bold flex items-center justify-center shrink-0"
                aria-hidden="true"
              >
                {currentTeam?.name.slice(0, 1).toUpperCase() ?? "?"}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[12.5px] font-semibold leading-tight truncate">
                  {currentTeam?.name ?? "No team"}
                </span>
                <span className="block font-mono text-[9.5px] text-faint">
                  {teamProjects.length}{" "}
                  {teamProjects.length === 1 ? "project" : "projects"}
                  {currentTeam?.isPersonal ? " · personal" : ""}
                </span>
              </span>
              <ChevronsUpDown className="size-3.5 text-faint shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-eyebrow">
              Switch team
            </DropdownMenuLabel>
            {teams.map((t) => (
              <DropdownMenuItem
                key={t.id}
                onSelect={() => navigate(`/teams/${t.id}`)}
              >
                <span
                  className="size-5 rounded bg-ink text-paper text-[10px] font-bold flex items-center justify-center"
                  aria-hidden="true"
                >
                  {t.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="flex-1 truncate">{t.name}</span>
                {t.role === "owner" && (
                  <span className="font-mono text-[9px] text-signal-red">
                    OWNER
                  </span>
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onCreateTeam?.()}>
              + New team
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/teams")}>
              All teams & projects
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Projects */}
      <div className="px-3 pb-3">
        <div className="text-eyebrow px-1 pb-1.5">Projects</div>
        <div className="bg-panel rounded-md p-1 space-y-0.5">
          {teamProjects.length === 0 && (
            <div className="px-2 py-2 text-xs text-muted-foreground italic">
              No projects yet.
            </div>
          )}
          {teamProjects.map((p) => {
            const active = p.id === currentProject?.id;
            return (
              <Link
                key={p.id}
                to={`/teams/${currentTeam!.id}/projects/${p.referenceId}`}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded text-[12.5px]",
                  active
                    ? "bg-card-paper shadow-[0_0_0_1px_var(--color-rule)]"
                    : "hover:bg-card-paper/60",
                )}
              >
                <span
                  className="size-1.5 rounded-sm shrink-0"
                  style={{ background: p.color ?? "#C8362C" }}
                />
                <span className={cn("flex-1 truncate", active && "font-semibold")}>
                  {p.name}
                </span>
                {active && (
                  <span className="font-mono text-[9px] text-signal-red">●</span>
                )}
              </Link>
            );
          })}
          {currentTeam && (
            <button
              onClick={() => onCreateProject?.(currentTeam.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-faint hover:text-foreground"
            >
              <span className="size-1.5 rounded-sm border border-dashed border-faint" />
              + New project
            </button>
          )}
        </div>
      </div>

      {/* Tools */}
      {currentProject && (
        <div className="flex-1 px-3 overflow-auto">
          <div className="text-eyebrow px-1 py-1.5">Tools</div>
          {TOOLS.map((it) => {
            const active = isActiveTool(it.pathSuffix);
            const to = `/teams/${currentTeam!.id}/projects/${currentProject.referenceId}${it.pathSuffix}`;
            return (
              <Link
                key={it.id}
                to={to}
                className={cn(
                  "flex items-center justify-between px-2.5 py-2 rounded text-[13.5px]",
                  active
                    ? "bg-ink text-paper font-medium"
                    : "hover:bg-panel/60",
                )}
              >
                <span>{it.label}</span>
                <span
                  className={cn(
                    "font-mono text-[9.5px]",
                    active ? "text-paper/70" : "text-faint",
                  )}
                >
                  {it.n}
                </span>
              </Link>
            );
          })}
        </div>
      )}
      {!currentProject && <div className="flex-1" />}

      {/* User card */}
      <div className="border-t border-rule p-3 flex items-center gap-2">
        <span
          className="size-6 rounded-full bg-signal-lavender text-paper text-[10px] font-bold flex items-center justify-center shrink-0"
          aria-hidden="true"
        >
          {(session?.user.name ?? session?.user.email ?? "?")
            .slice(0, 2)
            .toUpperCase()}
        </span>
        <div className="flex-1 min-w-0 text-[12px]">
          <div className="font-semibold truncate">
            {session?.user.name ?? session?.user.email}
          </div>
          <div className="font-mono text-[9.5px] text-faint truncate">
            {session?.user.email}
          </div>
        </div>
        <button
          onClick={toggleTheme}
          className="p-1 rounded hover:bg-panel"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? (
            <Sun className="size-4" />
          ) : (
            <Moon className="size-4" />
          )}
        </button>
        <button
          onClick={() =>
            onLogout?.() ??
            authClient.signOut({
              fetchOptions: {
                onSuccess: () => {
                  window.location.href = "/login";
                },
              },
            })
          }
          className="p-1 rounded hover:bg-panel"
          aria-label="Sign out"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </aside>
  );
}
